import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApi } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import BackButton from '../components/BackButton';
import { isGameOver } from '../utils/gameUtils';

interface LeaderboardEntry {
    rank: number;
    completedSquares: number;
    bingoLines: number;
    score: number;
    gridStatus?: { row: number; col: number; completed: boolean }[];
    /** Only present when the game is completed */
    userName?: string;
}

export default function RevealPage() {
    const { gameId } = useParams();
    const api = useApi();
    const { tenant } = useTenant();
    const timezone = (tenant as any)?.timezone || 'America/New_York';

    const [game, setGame] = useState<any>(null);
    const [topPlayers, setTopPlayers] = useState<LeaderboardEntry[]>([]);
    const [totalParticipants, setTotalParticipants] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [revealStarted, setRevealStarted] = useState(false);
    const [timeUntilReveal, setTimeUntilReveal] = useState<number>(0);

    useEffect(() => {
        if (gameId) {
            loadRevealData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameId]);

    async function loadRevealData() {
        try {
            setLoading(true);
            setError(null);

            // Load game details
            const gameResult = await api.getGame(gameId!);
            const loadedGame = gameResult.game;
            setGame(loadedGame);

            // Check if reveal time has passed
            if (loadedGame.revealSchedule) {
                const now = new Date();
                const revealTime = getNextRevealTime(loadedGame.revealSchedule);

                if (revealTime && revealTime > now) {
                    setRevealStarted(false);
                    setTimeUntilReveal(revealTime.getTime() - now.getTime());
                    setLoading(false);
                    return; // Don't load leaderboard if reveal hasn't started
                }
            }

            setRevealStarted(true);

            // Load leaderboard
            const leaderboardResult = await api.getGameLeaderboard(gameId!);
            setTopPlayers(leaderboardResult.topPlayers || []);
            setTotalParticipants(leaderboardResult.totalParticipants || 0);

        } catch (err: any) {
            console.error('Failed to load reveal data:', err);
            setError(err.message || 'Failed to load reveal data');
        } finally {
            setLoading(false);
        }
    }

    // Helper to calculate next reveal time using tenant timezone
    function getNextRevealTime(schedule: any): Date | null {
        if (!schedule?.enabled || !schedule?.time) return null;

        try {
            const [scheduleHour, scheduleMinute] = schedule.time.split(':').map(Number);
            const now = new Date();

            // Get current time in the specified timezone
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                hour12: false,
            });

            const parts = formatter.formatToParts(now);
            const currentHour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
            const currentMinute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');

            // Calculate total minutes to compare
            const currentTotalMinutes = currentHour * 60 + currentMinute;
            const scheduleTotalMinutes = scheduleHour * 60 + (scheduleMinute || 0);
            const durationMinutes = (schedule.durationHours || 1) * 60;

            // Check if reveal is currently active
            if (currentTotalMinutes >= scheduleTotalMinutes &&
                currentTotalMinutes < scheduleTotalMinutes + durationMinutes) {
                return null; // Return null to indicate reveal is active now
            }

            // Calculate time until next reveal
            let minutesUntil = scheduleTotalMinutes - currentTotalMinutes;
            if (minutesUntil <= 0) {
                minutesUntil += 24 * 60; // Next day
            }

            const nextReveal = new Date(now.getTime() + minutesUntil * 60 * 1000);
            return nextReveal;
        } catch {
            return null;
        }
    }

    // Countdown timer
    useEffect(() => {
        if (!revealStarted && timeUntilReveal > 0) {
            const timer = setInterval(() => {
                setTimeUntilReveal(prev => {
                    if (prev <= 1000) {
                        loadRevealData(); // Reload when countdown reaches 0
                        return 0;
                    }
                    return prev - 1000;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revealStarted, timeUntilReveal]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-text-secondary">Loading reveal...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Oops!</h1>
                    <p className="text-text-secondary mb-4">{error}</p>
                    <Link to="/games" className="text-primary hover:underline">
                        ← Back to Games
                    </Link>
                </div>
            </div>
        );
    }

    // Show countdown if reveal hasn't started
    if (!revealStarted && timeUntilReveal > 0) {
        const hours = Math.floor(timeUntilReveal / (1000 * 60 * 60));
        const minutes = Math.floor((timeUntilReveal % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeUntilReveal % (1000 * 60)) / 1000);

        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
                <div className="text-center">
                    <BackButton label="Back to Games" to="/games" className="mb-8 text-white" />

                    <div className="text-6xl mb-6">⏳</div>
                    <h1 className="text-3xl font-bold text-white mb-4">
                        Reveal Coming Soon!
                    </h1>
                    <p className="text-purple-200 mb-8">
                        {game?.name} - Results will be revealed in:
                    </p>

                    <div className="flex justify-center gap-4 mb-8">
                        <div className="bg-white/10 rounded-lg p-4 min-w-[80px]">
                            <div className="text-4xl font-bold text-white">{hours.toString().padStart(2, '0')}</div>
                            <div className="text-purple-300 text-sm">hours</div>
                        </div>
                        <div className="text-4xl font-bold text-white self-center">:</div>
                        <div className="bg-white/10 rounded-lg p-4 min-w-[80px]">
                            <div className="text-4xl font-bold text-white">{minutes.toString().padStart(2, '0')}</div>
                            <div className="text-purple-300 text-sm">min</div>
                        </div>
                        <div className="text-4xl font-bold text-white self-center">:</div>
                        <div className="bg-white/10 rounded-lg p-4 min-w-[80px]">
                            <div className="text-4xl font-bold text-white">{seconds.toString().padStart(2, '0')}</div>
                            <div className="text-purple-300 text-sm">sec</div>
                        </div>
                    </div>

                    <p className="text-purple-300 text-sm">
                        Check back when the countdown ends to see the results!
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-100 py-8">
            <div className="max-w-4xl mx-auto px-4">
                {/* Header */}
                <div className="flex flex-col items-center mb-8 relative">
                    <div className="absolute left-0 top-0">
                        <BackButton to="/" label="Back to Dashboard" className="!text-gray-900 hover:!text-black font-semibold" />
                    </div>
                    <div className="text-center pt-12 sm:pt-0">
                        {game && isGameOver(game) ? (
                            <>
                                <div className="text-5xl mb-4">🏆</div>
                                <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
                                    Final Results
                                </h1>
                                <p className="text-lg text-gray-600 mb-2">
                                    The game has ended — here are the top performers!
                                </p>
                            </>
                        ) : (
                            <>
                                <div className="text-5xl mb-4">👀</div>
                                <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">
                                    Game Result
                                </h1>
                                <p className="text-lg text-gray-600 mb-2">
                                    See how the top participants are doing!
                                </p>
                            </>
                        )}
                        {game && (
                            <p className="text-sm text-primary font-medium">
                                {game.name}
                            </p>
                        )}
                    </div>
                </div>

                {/* Top 3 Podium */}
                {topPlayers.length >= 1 && (
                    <div className="mb-12">
                        <div className="flex justify-center items-end gap-3 sm:gap-6">
                            {/* 2nd Place */}
                            {topPlayers.length >= 2 && (
                                <PodiumCard player={topPlayers[1]} place={2} />
                            )}
                            {/* 1st Place */}
                            <PodiumCard player={topPlayers[0]} place={1} />
                            {/* 3rd Place */}
                            {topPlayers.length >= 3 && (
                                <PodiumCard player={topPlayers[2]} place={3} />
                            )}
                        </div>
                    </div>
                )}

                {/* Stats */}
                <div className="bg-white/80 backdrop-blur rounded-2xl shadow-lg p-6 mb-8">
                    <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                            <div className="text-4xl font-bold text-primary">{totalParticipants}</div>
                            <div className="text-sm text-gray-600">Total Participants</div>
                        </div>
                        <div>
                            <div className="text-4xl font-bold text-green-600">
                                {topPlayers.reduce((sum, p) => sum + p.bingoLines, 0)}
                            </div>
                            <div className="text-sm text-gray-600">Total Bingo Lines</div>
                        </div>
                    </div>
                </div>

                {/* Bingo Grids for Top 3 */}
                {topPlayers.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-xl font-bold text-center text-gray-700 mb-6">
                            🎯 Their Bingo Cards
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {topPlayers.slice(0, 3).map((player, index) => (
                                <PlayerGridCard
                                    key={`player - ${index} `}
                                    player={player}
                                    place={index + 1}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Encouragement */}
                {topPlayers.length === 0 && (
                    <div className="text-center py-12 bg-white/60 rounded-2xl">
                        <div className="text-6xl mb-4">🎯</div>
                        <h2 className="text-xl font-bold text-gray-700 mb-2">
                            No completions yet!
                        </h2>
                        <p className="text-gray-500">
                            Be the first to complete bingo squares and claim the top spot!
                        </p>
                    </div>
                )}


            </div>
        </div>
    );
}

function PodiumCard({ player, place }: { player: LeaderboardEntry; place: number }) {
    const config = {
        1: {
            medal: '🥇',
            bg: 'bg-gradient-to-br from-yellow-400 to-amber-500',
            height: 'h-56',
            textSize: 'text-lg',
        },
        2: {
            medal: '🥈',
            bg: 'bg-gradient-to-br from-gray-300 to-gray-400',
            height: 'h-44',
            textSize: 'text-base',
        },
        3: {
            medal: '🥉',
            bg: 'bg-gradient-to-br from-orange-400 to-orange-500',
            height: 'h-36',
            textSize: 'text-base',
        },
    }[place] || { medal: '', bg: 'bg-gray-200', height: 'h-36', textSize: 'text-base' };

    return (
        <div
            className={`${config.height} w-28 sm:w-40 ${config.bg} rounded-xl shadow-xl p-4 text-white flex flex-col items-center justify-center transition hover:scale-105 cursor-default`}
        >
            <div className="text-4xl sm:text-5xl mb-2">{config.medal}</div>
            <div className={`${config.textSize} font-bold text-center mb-1 truncate w-full`}>
                {player.userName ?? `Position #${place}`}
            </div>
            <div className="text-xs sm:text-sm opacity-90">
                {player.bingoLines} {player.bingoLines === 1 ? 'Bingo' : 'Bingos'}
            </div>
            <div className="text-xs opacity-75">
                {player.completedSquares} squares
            </div>
        </div>
    );
}

function PlayerGridCard({ player, place }: { player: LeaderboardEntry; place: number }) {
    const medals = ['🥇', '🥈', '🥉'];
    const gridStatus = player.gridStatus || [];
    const gridSize = Math.sqrt(gridStatus.length) || 5;

    return (
        <div className="bg-white/90 backdrop-blur rounded-xl shadow-lg p-4">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{medals[place - 1] || `#${place}`}</span>
                <div>
                    <div className="font-bold text-gray-800">
                        {player.userName ?? `Position #${place}`}
                    </div>
                    <div className="text-xs text-gray-500">
                        {player.bingoLines} bingo{player.bingoLines !== 1 ? 's' : ''} • {player.completedSquares} squares
                    </div>
                </div>
            </div>

            {/* Grid */}
            <div
                className="grid gap-1"
                style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }}
            >
                {gridStatus.map((cell, index) => (
                    <div
                        key={index}
                        className={`aspect-square rounded transition-all ${cell.completed
                            ? 'bg-green-500 shadow-sm'
                            : 'bg-gray-200'
                            }`}
                        title={cell.completed ? 'Completed' : 'Not completed'}
                    />
                ))}
            </div>
        </div>
    );
}
